/**
 * Public agent invitation registration page.
 * Accessible at /t/:tenantId/invite/agent?token=...
 *
 * No auth required. Verifies the invite token and lets the new agent set a password.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { kongBrowserFetchUrl } from "@/lib/kongApi";

const PASSWORD_MIN = 8;
const PASSWORD_MAX = 128;

/** Post-registration redirect — set `VITE_TENANT_DASHBOARD_URL` at build time. */
const TENANT_DASHBOARD_URL = (import.meta.env?.VITE_TENANT_DASHBOARD_URL || "").trim();

/**
 * @param {string} tenantId
 * @param {string} token
 * @returns {Promise<{ email: string, workspaceName: string, tenantDisplayName: string, expiresAt: string }>}
 */
async function verifyInvite(tenantId, token) {
  const q = new URLSearchParams({ tenantId, token });
  const res = await fetch(kongBrowserFetchUrl(`/api/v1/public/agent-invitations/verify?${q}`), {
    headers: { Accept: "application/json" },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = body?.detail?.error || body?.error || body?.message || res.statusText;
    throw new Error(typeof msg === "string" ? msg : "Invalid or expired invitation.");
  }
  return body;
}

/**
 * @param {{ tenantId: string, token: string, password: string, displayName?: string }} params
 */
async function acceptInvite({ tenantId, token, password, displayName }) {
  const res = await fetch(kongBrowserFetchUrl("/api/v1/public/agent-invitations/accept"), {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ tenantId, token, password, displayName: displayName || undefined }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = body?.detail?.message || body?.detail?.error || body?.error || res.statusText;
    throw new Error(typeof msg === "string" ? msg : "Failed to complete registration.");
  }
  return body;
}

function PasswordStrengthBar({ password }) {
  const score = (() => {
    if (!password || password.length < PASSWORD_MIN) return 0;
    let s = 0;
    if (password.length >= 12) s += 1;
    if (/[A-Z]/.test(password)) s += 1;
    if (/[0-9]/.test(password)) s += 1;
    if (/[^A-Za-z0-9]/.test(password)) s += 1;
    return s;
  })();
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["", "bg-rose-400", "bg-amber-400", "bg-teal-400", "bg-emerald-500"];
  if (!password) return null;
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <div className="flex flex-1 gap-1">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all ${i <= score ? colors[score] : "bg-slate-200"}`}
          />
        ))}
      </div>
      <span className={`shrink-0 text-[10px] font-semibold ${score <= 1 ? "text-rose-500" : score === 2 ? "text-amber-500" : "text-emerald-600"}`}>
        {labels[score] || ""}
      </span>
    </div>
  );
}

const PREVIEW_INVITE = {
  email: "jane.doe@company.com",
  workspaceName: "Sales Support",
  tenantDisplayName: "Acme Corp",
};

export default function AgentInvitePage() {
  const { tenantId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const isPreview = searchParams.get("preview") === "1";

  const [verifyStatus, setVerifyStatus] = useState(/** @type {"loading"|"ok"|"error"} */ ("loading"));
  const [verifyError, setVerifyError] = useState("");
  const [invite, setInvite] = useState(/** @type {{ email: string, workspaceName: string, tenantDisplayName: string } | null} */ (null));

  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitStatus, setSubmitStatus] = useState(/** @type {"idle"|"loading"|"success"|"error"} */ ("idle"));
  const [submitError, setSubmitError] = useState("");
  const [showPass, setShowPass] = useState(false);

  // Verify token on mount
  useEffect(() => {
    if (isPreview) {
      setInvite(PREVIEW_INVITE);
      setVerifyStatus("ok");
      return;
    }
    if (!tenantId || !token) {
      setVerifyStatus("error");
      setVerifyError("Invalid invitation link.");
      return;
    }
    verifyInvite(tenantId, token)
      .then((data) => {
        setInvite(data);
        setVerifyStatus("ok");
      })
      .catch((err) => {
        setVerifyStatus("error");
        setVerifyError(err instanceof Error ? err.message : "Invalid or expired invitation.");
      });
  }, [isPreview, tenantId, token]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (password.length < PASSWORD_MIN) {
        setSubmitError(`Password must be at least ${PASSWORD_MIN} characters.`);
        return;
      }
      if (password.length > PASSWORD_MAX) {
        setSubmitError(`Password must not exceed ${PASSWORD_MAX} characters.`);
        return;
      }
      if (password !== confirm) {
        setSubmitError("Passwords do not match.");
        return;
      }
      setSubmitError("");
      setSubmitStatus("loading");
      try {
        if (isPreview) {
          await new Promise((r) => setTimeout(r, 800));
          setSubmitStatus("success");
          return;
        }
        await acceptInvite({
          tenantId: String(tenantId),
          token,
          password,
          displayName: displayName.trim() || undefined,
        });
        setSubmitStatus("success");
      } catch (err) {
        setSubmitStatus("error");
        setSubmitError(err instanceof Error ? err.message : "Registration failed. Please try again.");
      }
    },
    [isPreview, tenantId, token, password, confirm, displayName]
  );

  if (verifyStatus === "loading") {
    return (
      <PageShell>
        <div className="flex items-center justify-center py-20 text-slate-500 text-sm">
          <i className="ri-loader-4-line mr-2 animate-spin" /> Verifying invitation…
        </div>
      </PageShell>
    );
  }

  if (verifyStatus === "error") {
    return (
      <PageShell>
        <div className="flex flex-col items-center py-16 text-center">
          <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-500">
            <i className="ri-error-warning-line text-2xl" />
          </span>
          <h2 className="text-base font-semibold text-slate-900">Invitation unavailable</h2>
          <p className="mt-1.5 max-w-xs text-sm text-slate-500">{verifyError}</p>
        </div>
      </PageShell>
    );
  }

  if (submitStatus === "success") {
    return (
      <PageShell>
        <div className="flex flex-col items-center py-16 text-center">
          <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <i className="ri-checkbox-circle-line text-2xl" />
          </span>
          <h2 className="text-base font-semibold text-slate-900">Account created!</h2>
          <p className="mt-1.5 max-w-xs text-sm text-slate-500">
            Your agent account for <strong>{invite?.workspaceName}</strong> is ready. You can now
            sign in with your email and the password you just set.
          </p>
          {TENANT_DASHBOARD_URL ? (
            <a
              href={TENANT_DASHBOARD_URL}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-teal-600 px-6 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-teal-700"
            >
              Go to Dashboard
              <i className="ri-arrow-right-line text-base" />
            </a>
          ) : null}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="w-full max-w-md mx-auto">
        {/* Workspace badge */}
        <div className="mb-6 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700 ring-1 ring-teal-200/60">
            <i className="ri-building-2-line" />
            {invite?.workspaceName || "Workspace"}
          </span>
        </div>

        <h1 className="text-2xl font-bold tracking-tight text-slate-900 text-center">
          Set up your agent account
        </h1>
        <p className="mt-2 text-center text-sm text-slate-500">
          You've been invited to{" "}
          <span className="font-medium text-slate-700">{invite?.tenantDisplayName || "the platform"}</span>.
          Choose a password to complete your registration.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4" noValidate>
          {/* Email — read-only */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Email address
            </label>
            <input
              type="email"
              value={invite?.email ?? ""}
              readOnly
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600 cursor-not-allowed focus:outline-none"
            />
          </div>

          {/* Display name — optional */}
          <div>
            <label htmlFor="invite-display-name" className="block text-xs font-semibold text-slate-700 mb-1.5">
              Display name <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              id="invite-display-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name as shown to customers"
              maxLength={128}
              className="ui-input w-full"
              disabled={submitStatus === "loading"}
              autoComplete="name"
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="invite-password" className="block text-xs font-semibold text-slate-700 mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                id="invite-password"
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (submitError) setSubmitError("");
                }}
                placeholder={`Minimum ${PASSWORD_MIN} characters`}
                minLength={PASSWORD_MIN}
                maxLength={PASSWORD_MAX}
                required
                disabled={submitStatus === "loading"}
                autoComplete="new-password"
                className="ui-input w-full !pr-10"
              />
              <button
                type="button"
                aria-label={showPass ? "Hide password" : "Show password"}
                tabIndex={-1}
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <i className={showPass ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
              </button>
            </div>
            <PasswordStrengthBar password={password} />
          </div>

          {/* Confirm password */}
          <div>
            <label htmlFor="invite-confirm" className="block text-xs font-semibold text-slate-700 mb-1.5">
              Confirm password
            </label>
            <input
              id="invite-confirm"
              type={showPass ? "text" : "password"}
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                if (submitError) setSubmitError("");
              }}
              placeholder="Re-enter your password"
              minLength={PASSWORD_MIN}
              maxLength={PASSWORD_MAX}
              required
              disabled={submitStatus === "loading"}
              autoComplete="new-password"
              className="ui-input w-full"
            />
          </div>

          {submitError ? (
            <p className="rounded-lg bg-rose-50 px-3.5 py-2.5 text-xs text-rose-700 ring-1 ring-rose-200">
              {submitError}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={submitStatus === "loading" || !password || !confirm}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-teal-700 disabled:opacity-60"
          >
            {submitStatus === "loading" ? (
              <>
                <i className="ri-loader-4-line animate-spin" /> Creating account…
              </>
            ) : (
              "Create my account"
            )}
          </button>
        </form>
      </div>
    </PageShell>
  );
}

/** Shared page shell — centered, branded, no auth required. */
function PageShell({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-slate-50 via-white to-teal-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-teal-950/20">
      <header className="flex shrink-0 items-center gap-3 border-b border-slate-100 bg-white/80 px-6 py-4 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/85">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-600">
          <i className="ri-customer-service-2-line text-sm text-white" />
        </span>
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">SentroDesk</span>
      </header>
      <main className="app-content-surface flex flex-1 items-start justify-center px-4 py-12">{children}</main>
    </div>
  );
}
